import { Controller } from "@hotwired/stimulus"

// Reusable accordion: collapses/expands a panel beneath a heading trigger.
// One instance per collapsible block — instances do not coordinate, so multiple
// sections can be open at once (independent toggle). When a `key` value is set,
// open/closed state is persisted in localStorage under "accordion:<key>".
export default class extends Controller {
  static targets = ["panel", "trigger", "icon"]
  static values = { key: String, open: { type: Boolean, default: true } }

  connect() {
    this.apply(this.initialOpen(), false)
  }

  toggle() {
    this.apply(!this.isOpen, true)
    if (this.keyValue) {
      localStorage.setItem(this.storageKey, this.isOpen ? "open" : "closed")
    }
  }

  initialOpen() {
    if (this.keyValue) {
      const stored = localStorage.getItem(this.storageKey)
      if (stored !== null) return stored === "open"
    }
    return this.openValue
  }

  apply(open, animate) {
    this.isOpen = open
    this.triggerTarget.setAttribute("aria-expanded", open ? "true" : "false")
    if (this.hasIconTarget) this.iconTarget.classList.toggle("rotate-180", open)

    const panel = this.panelTarget
    if (open) {
      panel.classList.remove("hidden")
      if (animate) {
        requestAnimationFrame(() => panel.classList.remove("opacity-0", "translate-y-2"))
      } else {
        panel.classList.remove("opacity-0", "translate-y-2")
      }
    } else if (animate) {
      panel.classList.add("opacity-0", "translate-y-2")
      panel.addEventListener("transitionend", () => {
        if (!this.isOpen) panel.classList.add("hidden")
      }, { once: true })
    } else {
      panel.classList.add("opacity-0", "translate-y-2", "hidden")
    }
  }

  get storageKey() {
    return `accordion:${this.keyValue}`
  }
}
